import { jsx as _jsx } from "react/jsx-runtime";
import useInterval from '../../../Hooks/UseInterval';
import useWindowSize from '../../../Hooks/UseWindowSize';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AnimationTimings } from '../../../Constants/Animation';
import deepEqual from 'deep-equal';
const getWindowRect = () => {
    const top = window.scrollY;
    const left = window.scrollX;
    const bottom = top + window.innerHeight;
    const right = left + window.innerWidth;
    return { top, left, bottom, right };
};
const defaultProps = {
    id: 'highlight-overlay',
    className: 'highlight-overlay',
    colour: '#000000',
    shownOpacity: 0.4,
    padding: 0,
    zIndex: 1000,
    allowClickthrough: true,
    watchTargetRectPollTime: 100,
};
export const DEFAULT_HIGHLIGHT_TRANSITION_TIME = 400;
export default function Highlight(props) {
    const { target, onShow, onHide } = props;
    const [windowWidth, windowHeight] = useWindowSize();
    const [opacity, setOpacity] = useState(0);
    const [windowRect, setWindowRect] = useState(getWindowRect());
    const [highlightPath, setHighlightPath] = useState({ path: '', transition: '' });
    const [visible, setVisible] = useState(false);
    const [clickable, setClickable] = useState(false);
    const [targetRect, setTargetRect] = useState();
    const [transitioning, setTransitioning] = useState(false);
    const [pathFillColour, setPathFillColour] = useState();
    useEffect(() => {
        setWindowRect(() => getWindowRect());
    }, [windowWidth, windowHeight]);
    // default props
    const { id, className, colour, shownOpacity, padding, zIndex, allowClickthrough, watchTargetRectPollTime } = {
        ...defaultProps,
        ...props,
    };
    // transitions
    const fadeIn = useMemo(() => ({
        time: props.fadeInTransition?.time || DEFAULT_HIGHLIGHT_TRANSITION_TIME,
        bezier: props.fadeInTransition?.bezier || AnimationTimings.productive,
    }), [props.fadeInTransition]);
    const fadeOut = useMemo(() => ({
        time: props.fadeOutTransition?.time || DEFAULT_HIGHLIGHT_TRANSITION_TIME,
        bezier: props.fadeOutTransition?.bezier || AnimationTimings.productive,
    }), [props.fadeOutTransition]);
    const pathTransitionVariables = useMemo(() => ({
        time: props.pathTransition?.time || DEFAULT_HIGHLIGHT_TRANSITION_TIME,
        bezier: props.pathTransition?.bezier || AnimationTimings.productive,
    }), [props.pathTransition]);
    const fadeInTransition = `opacity ${fadeIn.time}ms ${fadeIn.bezier}`;
    const fadeOutTransition = `opacity ${fadeOut.time}ms ${fadeOut.bezier}`;
    const pathTransition = `all ${pathTransitionVariables.time}ms ${pathTransitionVariables.bezier}`;
    const recalculatePath = useCallback((pathTarget) => getHighlightPath(pathTarget, padding), [padding]);
    const intervalCallback = useCallback(() => {
        if (target) {
            const newRect = target?.getBoundingClientRect();
            if (!transitioning && !deepEqual(newRect?.toJSON(), targetRect?.toJSON(), { strict: true })) {
                setTargetRect(newRect);
            }
        }
    }, [target, targetRect, transitioning]);
    useInterval(() => intervalCallback(), watchTargetRectPollTime);
    const lastTargetRect = useRef();
    const lastWindowRect = useRef();
    useEffect(() => {
        if (target && (targetRect != lastTargetRect.current || windowRect != lastWindowRect.current)) {
            lastTargetRect.current = targetRect;
            lastWindowRect.current = windowRect;
            const path = recalculatePath(target);
            setHighlightPath({
                path,
                transition: 'none',
            });
        }
    }, [targetRect, windowRect, target, recalculatePath]);
    const lastTarget = useRef();
    useEffect(() => {
        let timeout;
        if (target) {
            if (target != lastTarget.current) {
                lastTarget.current = target;
                const path = recalculatePath(target);
                setHighlightPath({
                    path,
                    transition: pathTransition,
                });
                setPathFillColour(colour);
                setVisible(true);
                setClickable(false);
                setTransitioning(true);
                timeout = setTimeout(() => {
                    if (allowClickthrough) {
                        setClickable(true);
                    }
                    setTransitioning(false);
                    onShow?.();
                }, pathTransitionVariables.time);
            }
        }
        else {
            setPathFillColour(colour);
            lastTarget.current = undefined;
            setTransitioning(true);
            setOpacity(0);
            timeout = setTimeout(() => {
                setVisible(false);
                setTransitioning(false);
                onHide?.();
            }, fadeOut.time);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [
        target,
        recalculatePath,
        pathTransitionVariables,
        allowClickthrough,
        onShow,
        onHide,
        colour,
        fadeOut,
        pathTransition,
    ]);
    useEffect(() => {
        if (visible) {
            setOpacity(shownOpacity);
        }
    }, [visible, shownOpacity]);
    // Set SVG by css style
    const SVGStyle = {
        width: windowRect.right + 'px',
        height: windowRect.bottom + 'px',
        transition: fadeInTransition,
    };
    const PathStyle = {
        transition: highlightPath.transition,
        pointerEvents: 'all',
        fill: pathFillColour,
    };
    const OverlayStyle = {
        display: 'block',
        position: 'absolute',
        transition: opacity === 0 ? fadeOutTransition : fadeInTransition,
        opacity: opacity,
        zIndex,
        top: '0',
        left: '0',
        pointerEvents: clickable ? 'none' : 'all',
        maxHeight: '100vh',
        maxWidth: '100vw',
        overflow: 'hidden',
    };
    return (_jsx("div", { style: OverlayStyle, id: id, className: className, children: visible && (_jsx("svg", { style: SVGStyle, children: _jsx("path", { style: PathStyle, d: highlightPath.path }) })) }));
}
export const getHighlightPath = (pathTarget, padding) => {
    const windowRect = getWindowRect();
    const highlightTarget = pathTarget;
    // Get bounds of highlight target
    const highlightBounds = highlightTarget.getBoundingClientRect();
    const offset = {
        top: highlightBounds.top + window.scrollY,
        left: highlightBounds.left + window.scrollX,
    };
    // Get four corners
    let top = offset.top - padding;
    let left = offset.left - padding;
    let bottom = top + highlightTarget.offsetHeight + padding * 2;
    let right = left + highlightTarget.offsetWidth + padding * 2;
    // Start path (outer bounds)
    let path = 'M' +
        windowRect.left +
        ',' +
        windowRect.top +
        ' L' +
        windowRect.right +
        ',' +
        windowRect.top +
        ' L' +
        windowRect.right +
        ',' +
        windowRect.bottom +
        ' L' +
        windowRect.left +
        ',' +
        windowRect.bottom +
        ' L' +
        windowRect.left +
        ',' +
        windowRect.top;
    // Build inner shape path
    top = offset.top - padding;
    left = offset.left - padding;
    bottom = top + highlightTarget.offsetHeight + padding * 2;
    right = left + highlightTarget.offsetWidth + padding * 2;
    // Add to path (inner bounds)
    path +=
        ' M' +
            left +
            ',' +
            top +
            ' L' +
            left +
            ',' +
            bottom +
            ' L' +
            right +
            ',' +
            bottom +
            ' L' +
            right +
            ',' +
            top +
            ' L' +
            left +
            ',' +
            top;
    return path;
};
//# sourceMappingURL=Highlight.js.map